import { withTimeout } from '@/lib/promise';

export interface LoginBrowserAttribution {
  loginBrowserUserAgent?: string;
  loginBrowserCometFingerprint?: string;
  loginBrowserCometToken?: string;
}

export const LOGIN_BROWSER_PARAM_KEYS = [
  'login_browser_ua',
  'login_browser_comet_fingerprint',
  'login_browser_comet_token',
] as const;

export const AUTH_CALLBACK_PARAM_KEYS = [
  'access_token',
  'refresh_token',
  'id_token',
  'expires_in',
  'login_attempt',
  ...LOGIN_BROWSER_PARAM_KEYS,
] as const;

const DEFAULT_WAIT_TIMEOUT_MS = 1500;
const DEFAULT_POLL_INTERVAL_MS = 50;

type CometlyGetter = (() => unknown) | undefined;

async function readCometlyValue(
  getter: CometlyGetter
): Promise<string | undefined> {
  if (typeof getter !== 'function') {
    return undefined;
  }

  try {
    const value = await getter();
    return typeof value === 'string' && value ? value : undefined;
  } catch {
    return undefined;
  }
}

export async function getLoginBrowserAttributionFromWindow(
  win: Window = window
): Promise<LoginBrowserAttribution> {
  const [loginBrowserCometFingerprint, loginBrowserCometToken] =
    await Promise.all([
      readCometlyValue(win.cometFingerprint),
      readCometlyValue(win.cometToken),
    ]);

  return {
    loginBrowserUserAgent: win.navigator.userAgent || undefined,
    loginBrowserCometFingerprint,
    loginBrowserCometToken,
  };
}

export function getLoginBrowserAttributionFromSearchParams(
  searchParams: URLSearchParams
): LoginBrowserAttribution {
  return {
    loginBrowserUserAgent: searchParams.get('login_browser_ua') || undefined,
    loginBrowserCometFingerprint:
      searchParams.get('login_browser_comet_fingerprint') || undefined,
    loginBrowserCometToken:
      searchParams.get('login_browser_comet_token') || undefined,
  };
}

export function appendLoginBrowserAttribution(
  searchParams: URLSearchParams,
  attribution?: LoginBrowserAttribution
): void {
  if (!attribution) return;

  if (attribution.loginBrowserUserAgent) {
    searchParams.set('login_browser_ua', attribution.loginBrowserUserAgent);
  }
  if (attribution.loginBrowserCometFingerprint) {
    searchParams.set(
      'login_browser_comet_fingerprint',
      attribution.loginBrowserCometFingerprint
    );
  }
  if (attribution.loginBrowserCometToken) {
    searchParams.set(
      'login_browser_comet_token',
      attribution.loginBrowserCometToken
    );
  }
}

export function extractNonAuthDeepLinkParams(
  searchParams: URLSearchParams
): Record<string, string> {
  const nonAuthParams: Record<string, string> = {};

  searchParams.forEach((value, key) => {
    if (
      !AUTH_CALLBACK_PARAM_KEYS.includes(
        key as (typeof AUTH_CALLBACK_PARAM_KEYS)[number]
      )
    ) {
      nonAuthParams[key] = value;
    }
  });

  return nonAuthParams;
}

export async function waitForLoginBrowserAttribution(options?: {
  win?: Window;
  timeoutMs?: number;
  pollIntervalMs?: number;
}): Promise<LoginBrowserAttribution> {
  const win = options?.win ?? window;
  const timeoutMs = options?.timeoutMs ?? DEFAULT_WAIT_TIMEOUT_MS;
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const deadline = Date.now() + timeoutMs;
  let attribution: LoginBrowserAttribution = {
    loginBrowserUserAgent: win.navigator.userAgent || undefined,
  };

  for (;;) {
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) return attribution;
    try {
      // A Cometly getter can return a promise that never settles
      attribution = await withTimeout(
        getLoginBrowserAttributionFromWindow(win),
        remainingMs
      );
    } catch {
      // Only the timeout can reject: each Cometly read catches its own errors
      return attribution;
    }
    if (
      attribution.loginBrowserCometFingerprint ||
      attribution.loginBrowserCometToken
    ) {
      return attribution;
    }
    await new Promise((resolve) => {
      win.setTimeout(
        resolve,
        Math.min(pollIntervalMs, Math.max(0, deadline - Date.now()))
      );
    });
  }
}
